'use client';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Srid } from '@/graphql/graphql';
import { useSrids } from '@/hooks/useSrids';
import { cn } from '@/lib/utils';
import { fuzzy } from 'fast-fuzzy';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

interface iSridSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const getLabel = (srid: Srid) => `${srid.name}${srid.srid} - ${srid.projection}`;
const LIMIT = 100;

const SridSelector: React.FunctionComponent<iSridSelectorProps> = ({ value, onChange, className }) => {
  const { t } = useTranslation('components');
  const srids = useSrids();
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const srid = srids.find((srid) => srid.srid.toString() === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('justify-between font-mono overflow-hidden whitespace-nowrap overflow-ellipsis', className)}>
          <span className='block w-full text-left overflow-hidden whitespace-nowrap overflow-ellipsis'>
            {srid ? getLabel(srid) : t('sridSelector.selectEpsg')}
          </span>
          <ChevronsUpDown className='opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='p-0 w-[320px]'>
        <Command>
          <CommandInput placeholder={t('sridSelector.searchEpsg')} className='h-9' value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{t('sridSelector.noEpsgFound')}</CommandEmpty>
            <CommandGroup>
              {srids
                .filter(
                  (srid) =>
                    getLabel(srid).toLowerCase().includes(search.toLowerCase().trim()) ||
                    fuzzy(getLabel(srid), search.toLowerCase().trim(), {}) > 0.5,
                )
                .slice(0, LIMIT)
                .map((srid) => (
                  <CommandItem
                    className='overflow-hidden whitespace-nowrap font-mono overflow-ellipsis'
                    key={srid.srid}
                    // FIGHT THE SYSTEM!!!!
                    value={srid.srid.toString() + ' ' + getLabel(srid)}
                    onSelect={(currentValue) => {
                      onChange(currentValue.split(' ')[0]);
                      setOpen(false);
                    }}>
                    <div
                      title={getLabel(srid)}
                      className='overflow-hidden whitespace-nowrap font-mono overflow-ellipsis'>
                      {getLabel(srid)}
                    </div>
                    {value === srid.srid.toString() ? <Check className={cn('ml-auto', 'opacity-100')} /> : null}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SridSelector;
